import { LeftOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
	Button,
	DatePicker,
	Image,
	Input,
	Modal,
	Select,
	Table,
	Tag,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import SelectInfinity from "components/select-infinity/SelectInfinity";
import { CAR_STATE, COMMON_STATUS } from "constants/constants";
import { Merchant } from "features/merchant/types";
import { useDispatch } from "react-redux";
import { APP_PATH } from "constants/path";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getMerchantsAPI } from "service/apis";
import { translate } from "utils";
import { formatPrice } from "utils/format";
import {
	createHotSaleCarsAsync,
	getListAvailableHotSaleCarsAsync,
} from "../redux/hot-sale.slice";
import {
	CREATE_MODAL_STEP,
	SPLIT_MERCHANT_NAME,
	WARNING_PRICE_LIMIT,
} from "./constant";
import {
	CreateHotSale,
	CreateHotSaleQueryParams,
	InfinityMerchantQueryParams,
} from "./type";
import { DatePickerProps } from "antd/es";
import { RangePickerProps } from "antd/lib/date-picker";
import moment, { Moment } from "moment";

interface ModalCreateProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	getList: () => void;
}

const merchantQueryDefault = {
	skip: 0,
	take: 10,
	status: COMMON_STATUS.enable.value,
};

const createHotSaleQueryDefault = {
	skip: 0,
	take: 10,
};

const defaultEmptyError = {
	step: "",
	content: "",
};

const defaultCarAvatar = "/images/default-car.png";

const ModalCreate = ({ isOpen, setIsOpen, getList }: ModalCreateProps) => {
	const dispatch = useDispatch();
	const [emptyError, setEmptyError] = useState(defaultEmptyError);
	const [stepCreate, setStepCreate] = useState(CREATE_MODAL_STEP.STEP_1);
	const [listHotSales, setListHotSales] = useState([]);
	const [merchantList, setMerchantList] = useState<Merchant[]>([]);
	const [hotSalesTotal, setHotSaleTotal] = useState(0);
	const [merchantQuery, setMerchantQuery] =
		useState<InfinityMerchantQueryParams>(merchantQueryDefault);
	const [isLoadingHotSale, setIsLoadingHotSale] = useState(false);
	const [merchantSelected, setMerchantSelected] = useState<string>("");
	const [createHotSaleQuery, setCreateHotSaleQuery] =
		useState<CreateHotSaleQueryParams>(createHotSaleQueryDefault);
	const [listHotSalesSelected, setListHotSalesSelected] = useState<
		CreateHotSale[]
	>([]);
	const [isShowConfirmPopup, setIsShowConfirmPopup] = useState(false);
	const [isFirstSubmit, setIsFirstSubmit] = useState(true);

	const [createHotSaleConfirmQuery, setCreateHotSaleConfirmQuery] =
		useState<CreateHotSaleQueryParams>(createHotSaleQueryDefault);

	useEffect(() => {
		if (!isOpen) handleUpdateMerchantQuery("");
	}, [isOpen]);

	const columns: ColumnsType<CreateHotSale> = [
		{
			title: translate("No"),
			dataIndex: "",
			key: "no",
			width: "50px",
			align: "center",
			render: (_, __, index: number) =>
				stepCreate === CREATE_MODAL_STEP.STEP_2 ? (
					<p>{createHotSaleQuery.skip + index + 1}</p>
				) : (
					<p>{createHotSaleConfirmQuery.skip + index + 1}</p>
				),
		},
		{
			title: translate("Image"),
			dataIndex: "cover",
			key: "cover",
			width: 200,
			render: image => (
				<div className="flex justify-center items-center">
					<Image
						className="object-cover aspect-video"
						src={image ?? defaultCarAvatar}
					/>
				</div>
			),
		},
		{
			title: translate("Title"),
			dataIndex: "name",
			key: "name",
			align: "center",
			render: (value, record) =>
				stepCreate === CREATE_MODAL_STEP.STEP_2 ? (
					<Link
						to={`${APP_PATH.cars}/${record.id}`}
						target="_blank"
						className="text-blue-500"
					>
						<p>{value}</p>
					</Link>
				) : (
					<p>{value}</p>
				),
		},
		{
			title: translate("Price"),
			dataIndex: "price",
			key: "no",
			align: "center",
			render: price => <p>{formatPrice(price)}</p>,
		},
		{
			title: translate("Discount"),
			dataIndex: "discountValue",
			key: "discountValue",
			align: "center",
			render: disCountPrice => <p>{formatPrice(disCountPrice)}</p>,
		},
		Table.SELECTION_COLUMN,
	];

	const handleCloseModal = () => {
		if (stepCreate === CREATE_MODAL_STEP.STEP_1 && !merchantSelected)
			setIsOpen(false);
		else {
			window.dialogConfirm(
				translate("Confirm close modal"),
				translate("If you close the modal, all data will be lost"),
				(isConfirm: boolean) => {
					if (isConfirm) {
						setIsOpen(false);
						setMerchantSelected("");
						setListHotSalesSelected([]);
						setEmptyError(defaultEmptyError);
						setStepCreate(CREATE_MODAL_STEP.STEP_1);
						setMerchantQuery(merchantQueryDefault);
						setCreateHotSaleQuery(createHotSaleQueryDefault);
						setCreateHotSaleConfirmQuery(createHotSaleQueryDefault);
					}
				},
				true,
			);
		}
	};

	const handleChangePage = (page: number) => {
		if (stepCreate === CREATE_MODAL_STEP.STEP_2) {
			const newQuery = {
				...createHotSaleQuery,
				skip: (page - 1) * createHotSaleQuery.take,
			};
			handleGetListAvaiableHotSaleCars(newQuery);
		} else {
			const newQuery = {
				...createHotSaleConfirmQuery,
				skip: (page - 1) * createHotSaleConfirmQuery.take,
			};
			setCreateHotSaleConfirmQuery(newQuery);
		}
	};

	const handlePageSizeChange = (_: number, size: number) => {
		setTimeout(() => {
			if (stepCreate === CREATE_MODAL_STEP.STEP_2) {
				const newQuery = { ...createHotSaleQuery, skip: 0, take: size };
				handleGetListAvaiableHotSaleCars(newQuery);
			} else {
				const newQuery = { ...createHotSaleConfirmQuery, skip: 0, take: size };
				setCreateHotSaleConfirmQuery(newQuery);
			}
		}, 0);
	};

	const handleSetMerchantSelected = (fullName: string) => {
		setMerchantSelected(fullName);
		setCreateHotSaleQuery(createHotSaleQueryDefault);
		setListHotSalesSelected([]);
	};

	const handleUpdateMerchantQuery = (fullName: string) => {
		const newMerchantQuery = {
			...merchantQuery,
			fullName: fullName || null,
			skip: 0,
		};
		setMerchantQuery(newMerchantQuery);
	};

	const handleGetListAvaiableHotSaleCars = (
		query: CreateHotSaleQueryParams,
	) => {
		const findMerchant = merchantList.find(
			merchant =>
				`${merchant.fullName} ${SPLIT_MERCHANT_NAME} ${merchant.username}` ===
				merchantSelected,
		);
		const newQuery = {
			...query,
			merchantId: findMerchant?.id,
		};
		setIsLoadingHotSale(true);
		dispatch(
			getListAvailableHotSaleCarsAsync({
				query: newQuery,
				callback: (response: any) => {
					setHotSaleTotal(response.total);
					setListHotSales(response.data);
					setIsLoadingHotSale(false);
					setCreateHotSaleQuery(newQuery);
				},
			}),
		);
	};

	const handleChangeHotSale = (e: React.ChangeEvent<HTMLInputElement>) => {
		const query = {
			...createHotSaleQuery,
			titleSearch: e.target.value.trim() || null,
		};
		setCreateHotSaleQuery(query);
	};

	const handleSearchHotSale = () => {
		const newQuery = {
			...createHotSaleQuery,
			name: createHotSaleQuery.titleSearch || null,
			skip: 0,
		};
		handleGetListAvaiableHotSaleCars(newQuery);
	};

	const validate = () => {
		for (let index = 0; index < listHotSalesSelected.length; index++) {
			if (
				!listHotSalesSelected[index].startDate ||
				!listHotSalesSelected[index].endDate
			)
				return false;
		}
		return true;
	};

	const handleCreateHotSaleCars = () => {
		if (isFirstSubmit) setIsFirstSubmit(false);

		if (!validate()) {
			return;
		}
		const hotSalesPayload = listHotSalesSelected.map(item => ({
			carId: item.id,
			startDate: item.startDate,
			endDate: item.endDate,
		}));
		dispatch(
			createHotSaleCarsAsync({
				hotSales: hotSalesPayload,
				callback: () => {
					setIsOpen(false);
					setIsFirstSubmit(true);
					getList();
					setMerchantSelected("");
					setStepCreate(CREATE_MODAL_STEP.STEP_1);
					setListHotSalesSelected([]);
					setCreateHotSaleQuery(createHotSaleQueryDefault);
					setCreateHotSaleConfirmQuery(createHotSaleQueryDefault);
				},
			}),
		);
	};

	const onSelect = (record: CreateHotSale, selected: boolean) => {
		if (selected) setListHotSalesSelected(prev => [...prev, record]);
		else
			setListHotSalesSelected(prev =>
				prev.filter(item => item.id !== record.id),
			);
	};

	const handleChangeActiveDate = (
		hotSale: CreateHotSale,
		dateString: string | [string, string],
	) => {
		setListHotSalesSelected(
			listHotSalesSelected.map(h => {
				if (h.id === hotSale.id) {
					return {
						...h,
						startDate: dateString[0],
						endDate: dateString[1],
					};
				}
				return h;
			}),
		);
	};

	const getModalProps = (type: string) => {
		switch (type) {
			case CREATE_MODAL_STEP.STEP_1: {
				return {
					footer: [
						<Button
							type="primary"
							className="bg-[#1890ff]"
							onClick={() => {
								if (!merchantSelected) {
									setEmptyError({
										step: CREATE_MODAL_STEP.STEP_1,
										content: translate("Please select merchant"),
									});
								} else {
									handleGetListAvaiableHotSaleCars(createHotSaleQuery);
									setEmptyError(defaultEmptyError);
									setStepCreate(CREATE_MODAL_STEP.STEP_2);
								}
							}}
						>
							{translate("Next")}
						</Button>,
					],
				};
			}
			case CREATE_MODAL_STEP.STEP_2: {
				return {
					width: "60vw",
					footer: [
						<div className="flex justify-end">
							<Button
								icon={<LeftOutlined />}
								className="!bg-[#595959] !text-white border-0 flex items-center"
								onClick={() => setStepCreate(CREATE_MODAL_STEP.STEP_1)}
							>
								{translate("Go back")}
							</Button>
							<Button
								type="primary"
								className="bg-[#1890ff]"
								onClick={() => {
									if (!listHotSalesSelected.length) {
										setEmptyError({
											step: CREATE_MODAL_STEP.STEP_2,
											content: translate("Please select list hot sale"),
										});
									} else {
										setEmptyError(defaultEmptyError);
										let isValid = true;
										for (let i = 0; i < listHotSalesSelected.length; i++) {
											if (
												(listHotSalesSelected[i].discountValue <
													WARNING_PRICE_LIMIT.NEW &&
													listHotSalesSelected[i].state ===
														CAR_STATE[0].value) ||
												(listHotSalesSelected[i].discountValue <
													WARNING_PRICE_LIMIT.USED &&
													listHotSalesSelected[i].state === CAR_STATE[1].value)
											) {
												setIsShowConfirmPopup(true);
												isValid = false;
											}
										}
										if (isValid) setStepCreate(CREATE_MODAL_STEP.STEP_3);
									}
								}}
							>
								{translate("Next")}
							</Button>
						</div>,
					],
				};
			}
			case CREATE_MODAL_STEP.STEP_3: {
				return {
					width: "60vw",
					footer: [
						<div className="flex justify-end">
							<Button
								icon={<LeftOutlined />}
								className="!bg-[#595959] !text-white border-0 flex items-center"
								onClick={() => setStepCreate(CREATE_MODAL_STEP.STEP_2)}
							>
								{translate("Go back")}
							</Button>
							<Button
								type="primary"
								className="bg-[#1890ff]"
								onClick={() => {
									handleCreateHotSaleCars();
								}}
							>
								{translate("Confirm")}
							</Button>
						</div>,
					],
				};
			}
			default:
				break;
		}
	};

	const getModalContent = (type: string) => {
		switch (type) {
			case CREATE_MODAL_STEP.STEP_1: {
				return (
					<div>
						<p>{translate("Merchant")}</p>
						<SelectInfinity
							allowClear
							showSearch
							className="w-full"
							placeholder={translate("Select merchant")}
							query={merchantQuery}
							setQuery={setMerchantQuery}
							renderList={merchantList}
							setRenderList={setMerchantList}
							fetchMoreFunc={getMerchantsAPI}
							value={merchantSelected || undefined}
							onChange={handleSetMerchantSelected}
							onSearch={handleUpdateMerchantQuery}
							handleOnClear={() => setMerchantQuery(merchantQueryDefault)}
							optionRender={(item: Merchant) => (
								<Select.Option
									value={`${item.fullName} ${SPLIT_MERCHANT_NAME} ${item.username}`}
									key={item.id}
								>
									<p>{item.fullName}</p>
									<span className="text-sm text-[#95a5a6]">
										{item.username}
									</span>
								</Select.Option>
							)}
						/>
						{emptyError.step === stepCreate ? (
							<p className="text-red-500 mt-4">{emptyError.content}</p>
						) : null}
					</div>
				);
			}
			case CREATE_MODAL_STEP.STEP_2: {
				return (
					<div>
						<p>
							<span>{translate("Please select cars from")}</span>
							<Tag color="blue" className="ml-2">
								{merchantSelected.split(SPLIT_MERCHANT_NAME)[0]}
							</Tag>
						</p>
						<div className="py-8 grid grid-cols-3">
							<div className="col-span-1">
								<p>{translate("Select for cars")}</p>
								<Input.Search
									value={createHotSaleQuery.titleSearch || ""}
									onChange={handleChangeHotSale}
									onSearch={handleSearchHotSale}
									placeholder={translate("Input car title")}
								/>
							</div>
						</div>
						<Table
							bordered
							scroll={{ y: "45vh" }}
							columns={columns}
							dataSource={listHotSales}
							loading={isLoadingHotSale}
							rowKey={record => record.id}
							rowSelection={{
								columnWidth: 100,
								columnTitle: translate("Select"),
								type: "checkbox",
								hideSelectAll: true,
								selectedRowKeys: listHotSalesSelected.map(row => row.id),
								onSelect: onSelect,
							}}
							pagination={{
								pageSizeOptions: ["5", "10", "20", "50"],
								total: hotSalesTotal,
								current: createHotSaleQuery.skip / createHotSaleQuery.take + 1,
								pageSize: createHotSaleQuery.take,
								showSizeChanger: true,
								showTotal: total =>
									translate("Total") + " " + total + " " + translate("results"),
								onChange: handleChangePage,
								onShowSizeChange: handlePageSizeChange,
							}}
							locale={{
								emptyText: (
									<span className="text-gray-8">{translate("No data")}</span>
								),
							}}
						/>
						{emptyError.step === stepCreate ? (
							<p className="text-red-500 mt-4">{emptyError.content}</p>
						) : null}
						<Modal
							maskClosable={false}
							centered
							style={{ top: -100 }}
							open={isShowConfirmPopup}
							onCancel={() => setIsShowConfirmPopup(false)}
							okText={translate("Confirm")}
							onOk={() => {
								setIsShowConfirmPopup(false);
								setStepCreate(CREATE_MODAL_STEP.STEP_3);
							}}
						>
							<div className="flex flex-col gap-2 w-full items-center mb-4">
								<span>
									<ExclamationCircleOutlined
										style={{ fontSize: "30px", color: "#faad14" }}
									/>
								</span>
								<h1 className="font-bold text-xl">
									{translate("Are you sure you want to create this?")}
								</h1>
							</div>
							<div className="px-6">
								<ul>
									{listHotSalesSelected.map(hotSale => (
										<>
											{(!hotSale.discountValue ||
												hotSale.discountValue < WARNING_PRICE_LIMIT.USED) &&
												hotSale.state === CAR_STATE[1].value && (
													<li className="list-disc">
														{translate("The car") +
															" " +
															hotSale.name +
															" " +
															translate(`doesn't have discount more than`) +
															" " +
															formatPrice(WARNING_PRICE_LIMIT.USED)}
													</li>
												)}
											{(!hotSale.discountValue ||
												hotSale.discountValue < WARNING_PRICE_LIMIT.NEW) &&
												hotSale.state === CAR_STATE[0].value && (
													<li className="list-disc">
														{translate("The car") +
															" " +
															hotSale.name +
															" " +
															translate(`doesn't have discount more than`) +
															" " +
															formatPrice(WARNING_PRICE_LIMIT.NEW)}
													</li>
												)}
										</>
									))}
								</ul>
							</div>
						</Modal>
					</div>
				);
			}
			case CREATE_MODAL_STEP.STEP_3: {
				return (
					<div>
						<p className="font-bold py-4">
							{translate("Please setup the active time of hot sale:")}
						</p>

						<div className="flex flex-col gap-4">
							{listHotSalesSelected.map((item: CreateHotSale, index) => (
								<div key={index} className="flex justify-between items-center">
									<div className="flex gap-4 items-center">
										<img
											src={item.cover || defaultCarAvatar}
											alt=""
											className="w-[50px] h-[50px] object-contain"
										/>
										<span>{item.name}</span>
									</div>
									<div className="w-1/2">
										<div className="mb-1">
											<span>{translate("Active time")}</span>
											<span className="text-red-500">*</span>
										</div>
										<DatePicker.RangePicker
											disabledDate={current =>
												current &&
												current < moment().subtract(1, "day").endOf("day")
											}
											className="w-full"
											onChange={(
												_: DatePickerProps["value"] | RangePickerProps["value"],
												dateString: string | [string, string],
											) => {
												handleChangeActiveDate(item, dateString);
											}}
										/>
										{!isFirstSubmit && (!item.startDate || !item.endDate) && (
											<span className="text-red-500">
												{translate("Invalid date range")}
											</span>
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				);
			}
			default:
				break;
		}
	};

	return (
		<div>
			<Modal
				destroyOnClose
				open={isOpen}
				maskClosable={false}
				title={translate("Create hot sale items")}
				onCancel={handleCloseModal}
				{...getModalProps(stepCreate)}
			>
				{getModalContent(stepCreate)}
			</Modal>
		</div>
	);
};

export default ModalCreate;
